import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { trpc } from "@/lib/trpc";
import { RefreshCw, Filter, TrendingDown, Users, ArrowDown } from "lucide-react";

type SincePeriod = "1h" | "24h" | "7d";

const PERIOD_OPTIONS: Array<{ value: SincePeriod; label: string }> = [
  { value: "1h", label: "1 heure" },
  { value: "24h", label: "24 heures" },
  { value: "7d", label: "7 jours" },
];

const STEP_LABELS: Record<string, string> = {
  pageView: "Visite",
  signupStarted: "Signup démarré",
  completedSignup: "Signup complet",
  emailVerified: "Email vérifié",
  firstAction: "1ère action",
};

function formatBucket(iso: string, since: SincePeriod): string {
  const d = new Date(iso);
  if (since === "1h") {
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  }
  if (since === "24h") {
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

export default function AcquisitionFunnel() {
  const [since, setSince] = useState<SincePeriod>("24h");

  const {
    data: funnelData,
    isLoading: funnelLoading,
    refetch: refetchFunnel,
  } = trpc.admin.analytics.getAcquisitionFunnel.useQuery({ since });

  const {
    data: timeseriesData,
    isLoading: timeseriesLoading,
    refetch: refetchTimeseries,
  } = trpc.admin.analytics.getSignupsTimeseries.useQuery({ since });

  useEffect(() => {
    const interval = setInterval(() => {
      refetchFunnel();
      refetchTimeseries();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [refetchFunnel, refetchTimeseries]);

  const refetchAll = () => {
    refetchFunnel();
    refetchTimeseries();
  };

  const maxTotal = funnelData?.[0]?.total ?? 1;

  const chartData = (timeseriesData ?? []).map((point) => ({
    bucket: formatBucket(point.bucket, since),
    signups: point.count,
  }));

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Filter className="h-6 w-6" />
              Santé acquisition
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Funnel d'acquisition et signups en temps réel
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={refetchAll}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Period switcher */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground font-medium">Période :</span>
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSince(opt.value)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                since === opt.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Funnel section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingDown className="h-4 w-4" />
              Funnel d'acquisition
            </CardTitle>
          </CardHeader>
          <CardContent>
            {funnelLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-14 w-full" />
                ))}
              </div>
            ) : !funnelData?.length ? (
              <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                <Users className="h-8 w-8 mb-2" />
                <p>Aucune donnée pour cette période</p>
              </div>
            ) : (
              <div className="space-y-3">
                {funnelData.map((step, idx) => {
                  const widthPct = maxTotal > 0 ? Math.max((step.total / maxTotal) * 100, 4) : 4;
                  const isHighDropoff = step.dropoffPct !== null && step.dropoffPct > 50;

                  return (
                    <div key={step.step}>
                      {/* Dropoff indicator between steps */}
                      {idx > 0 && step.dropoffPct !== null && (
                        <div
                          className={`flex items-center gap-1.5 text-xs mb-1.5 ml-2 ${
                            isHighDropoff ? "text-destructive font-semibold" : "text-muted-foreground"
                          }`}
                        >
                          <ArrowDown className="h-3 w-3" />
                          <span>
                            {isHighDropoff ? "Drop-off critique : " : "Drop-off : "}
                            {step.dropoffPct}%
                          </span>
                        </div>
                      )}

                      {/* Funnel bar */}
                      <div className="flex items-center gap-3">
                        <div className="w-36 text-sm font-medium text-right shrink-0">
                          {STEP_LABELS[step.name] ?? step.name}
                        </div>
                        <div className="flex-1 relative">
                          <div
                            className={`h-10 rounded-md flex items-center px-3 transition-all ${
                              isHighDropoff
                                ? "bg-destructive/15 border border-destructive/30"
                                : "bg-primary/10 border border-primary/20"
                            }`}
                            style={{ width: `${widthPct}%`, minWidth: "60px" }}
                          >
                            <span className="text-sm font-bold">
                              {step.total.toLocaleString("fr-FR")}
                            </span>
                          </div>
                        </div>
                        <div className="w-40 text-xs text-muted-foreground shrink-0">
                          {step.particulier !== null && step.pro !== null ? (
                            <span>
                              Part. {step.particulier.toLocaleString("fr-FR")} · Pro{" "}
                              {step.pro.toLocaleString("fr-FR")}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Timeseries section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              Signups par{" "}
              {since === "1h" ? "tranche de 5 min" : since === "24h" ? "heure" : "jour"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {timeseriesLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : !chartData.length ? (
              <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                <Users className="h-8 w-8 mb-2" />
                <p>Aucun signup sur cette période</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="bucket"
                    tick={{ fontSize: 12 }}
                    className="text-muted-foreground"
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 12 }}
                    className="text-muted-foreground"
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="signups"
                    name="Signups"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
